import { useMemo, useContext } from 'react';
import { ScreenClassContext } from 'react-grid-system';

import BREAKPOINT_LIST from 'ia-react-core/~/constants/BREAKPOINT_LIST';
import { Breakpoint } from 'ia-react-core/~/interfaces/Breakpoint';

type PickResponsive<P, K = keyof P> = {
  [Y in K extends `${infer Key}-${Breakpoint}` ? Key : never]: `${Y}-xs` extends keyof P ? P[`${Y}-xs`] : never
};

const useResponsiveProps = <
  Props extends object,
  ResponsiveProps extends readonly (keyof PickResponsive<Props>)[]
>(props: Props, responsiveProps: ResponsiveProps): PickResponsive<Props> => {
  const upperBreakpoint = useContext(ScreenClassContext);
  const upperBreakpointIndex = useMemo(() => BREAKPOINT_LIST.indexOf(upperBreakpoint), [upperBreakpoint]);

  const baseProps = useMemo(() => (
    responsiveProps.reduce((result, propName) => {
      result[propName] = props[propName as unknown as keyof Props];
      return result;
    }, {} as any)
  ), [props, responsiveProps]);

  const resolvedProps = useMemo(() => (
    BREAKPOINT_LIST.reduce((result, breakpoint) => {
      responsiveProps.forEach((propName) => {
        const index = BREAKPOINT_LIST.indexOf(breakpoint);
        const responsivePropName = `${propName}-${breakpoint}` as const;
        if (typeof props[responsivePropName as keyof Props] !== 'undefined' && upperBreakpointIndex >= index) {
          result[propName] = props[responsivePropName as keyof Props];
        }
      });
      return result;
    }, baseProps)
  ), [baseProps, props, responsiveProps, upperBreakpointIndex]);

  return resolvedProps;
};

/**
 * @component
 * @visibleName useResponsiveProps
 */
export default useResponsiveProps;
